/* eslint-disable max-lines */
import React, { Component } from "react";
import { compose } from "redux";
import { withTranslation } from "react-i18next";
import AppContext from "../AppContext";
import { priceTypes } from "../constants";
import SessionStore from "../store/SessionStore";
import withToast from "./withToast";
import withProductsPrices from "./withProductsPrices";
import withCustomers from "./withCustomers";
import withProducts from "./withProducts";
import withUsers from "./withUsers";
import withProductsDescriptions from "./withProductsDescriptions";
import withCurrentOrder from "./withCurrentOrder";
import withOrders from "./withOrders";
import withCustomersPriceGroup from "./withCustomersPriceGroup";
import withProductDeliveryPeriods from "./withProductDeliveryPeriods";
import withDeliveryPeriods from "./withDeliveryPeriods";
import withCustomerAddresses from "./withCustomerAddresses";
import withTischOffensives from "./withTischOffensives";
import withCollInfo from "./withCollInfo";
import withSeasons from "./withSeasons";
import withDeliveryOptions from "./withDeliveryOptions";
import { update, create } from "../utils/salesforce";
import i18n, { getTranslateByScope, changeAppLanguage } from "../i18n";
import { createCacheForPrice } from "../utils/createCacheForPrice";
import { generateOrderId } from "../utils/order";
import { setUsersCredentials } from "../utils/userCredentials";
import {
  getSalesforceConnectionByAccessToken,
  getSalesforceConnectionByCredentials,
} from "../utils/getSalesforceConnection";

const translate = getTranslateByScope("app");

const sessionDb = new SessionStore("sessionDb");

const loadLowaCustomer = () => {
  const data = localStorage.getItem("lowaCustomer");

  if (data && data.length === 0) {
    return null;
  }

  let record = null;
  try {
    record = JSON.parse(data);
    console.log("got a record");
    console.log(record);
  } catch (e) {
    console.log("could not parse lowa customer", e);
  }

  return record;
};

const initialState = () => ({
  session: {},
  startupLoading: true,
  priceType: localStorage.getItem("priceType") || priceTypes.unit,
  preloadingImagesEnable: false,
  cacheForPrice: JSON.parse(localStorage.getItem("cacheForPrice")) || {},
  online: window.navigator.onLine,
  marketingMode: false,
  lowa: false,
  lowaCustomer: loadLowaCustomer(),
});

class AppProvider extends Component {
  state = initialState();

  componentDidMount() {
    setTimeout(() => {
      this.setState({ startupLoading: false });
    }, 2000);
    this.fetchSession(() => {
      this.props.orders.load();
      this.props.currentOrder.load();
      this.props.customers.load();
      this.props.users.load();
      this.props.products.fetchIfCacheIsExpiredOtherwiseJustLoad();
      this.props.productDescriptions.fetchIfCacheIsExpiredOtherwiseJustLoad();
      this.props.productsPrices.fetchIfCacheIsExpiredOtherwiseJustLoad();
      this.props.productDeliveryPeriods.fetchIfCacheIsExpiredOtherwiseJustLoad();
      this.props.customerAddresses.fetchIfCacheIsExpiredOtherwiseJustLoad();
      this.props.tischOffensives.fetchIfCacheIsExpiredOtherwiseJustLoad();
      this.props.customerPriceGroups.fetchIfCacheIsExpiredOtherwiseJustLoad();
      this.props.deliveryPeriods.fetchIfCacheIsExpiredOtherwiseJustLoad();
      this.props.collInfo.fetchIfCacheIsExpiredOtherwiseJustLoad();
      this.props.seasons.fetchIfCacheIsExpiredOtherwiseJustLoad();
      this.props.deliveryOptions.fetchIfCacheIsExpiredOtherwiseJustLoad();

      window.addEventListener("offline", this.handleOffline);
      window.addEventListener("online", this.handleOffline);
    });
  }

  componentWillUnmount() {
    window.removeEventListener("offline", this.handleOffline);
    window.removeEventListener("online", this.handleOffline);
  }

  handleOffline = () => {
    this.setState({ online: window.navigator.onLine });
  };

  fetchSession(onSessionFetched = () => {}) {
    sessionDb.get((session) => {
      if (!session) {
        return;
      }
      this.setState({ session });
      onSessionFetched();
    });
  }

  login = async ({ email, password, onFailure = () => {} }) => {
    const conn = getSalesforceConnectionByCredentials();

    try {
      const userInfo = await conn.login(email, password);
      if (!userInfo.id) {
        return;
      }

      const soql = `SELECT id, CountryCode, Email, Username FROM User WHERE Id = '${userInfo.id}'`;
      const data = await conn.query(soql);
      const userData = data.records[0];
      const user = {
        id: userInfo.id,
        countryCode: userData.CountryCode,
      };

      const session = {
        user,
        accessToken: conn.accessToken,
        instanceUrl: conn.instanceUrl,
      };

      if (userData.countryCode === "NB") {
        changeAppLanguage("en");
        i18n.changeLanguage("en");
      }

      sessionDb.set(session, () => {
        this.fetchSession();
        this.downloadData(session);
      });

      setUsersCredentials(email, password);
    } catch (error) {
      onFailure(error);
    }
  };

  setCurrentSyncData = (progress, text) => {
    this.setState({ sync: { progress, text } });
  };

  onError = (error) => {
    if (error.message === "NOT_AUTHENTICATED") {
      this.setState({ session: false });
      this.setCurrentSyncData();
      this.props.toast.setToast({
        title: translate("sync.syncAuthError.title"),
        text: translate("sync.syncAuthError.description"),
      });
    }
  };

  downloadData = (session) => {
    global.productsByNumber = null;

    const completeSync = () => {
      this.props.toast.setToast({
        title: translate("sync.syncCompleted.title"),
        text: translate("sync.syncCompleted.description"),
      });
      /*
      Hotfix for customer  dropdown
      not showing customer data
      */
      setTimeout(() => {
        window.location.reload();
      }, 3000);
    };

    const fetchProductsDescriptions = () => {
      this.props.productDescriptions.fetch(completeSync, this.onError);
      this.setCurrentSyncData(
        90,
        translate("sync.progress.fetchingProductsDescriptions"),
      );
    };

    const fetchProducts = () => {
      this.props.products.fetch(() => {
        this.enablePreloadingImages(true);
        fetchProductsDescriptions();
      }, this.onError);
      this.setCurrentSyncData(85, translate("sync.progress.fetchingProducts"));
    };

    const fetchUsers = () => {
      this.props.users.fetch(session, fetchProducts, this.onError);
      this.setCurrentSyncData(80, translate("sync.progress.fetchingUsers"));
    };

    const fetchCustomers = () => {
      this.setCurrentSyncData(70, translate("sync.progress.fetchingCustomers"));
      this.props.customers.fetch(session, fetchUsers, () => {
        this.logout();
      });
    };

    const fetchTischOffensives = () => {
      this.props.tischOffensives.fetch(fetchCustomers, this.onError);
      this.setCurrentSyncData(
        60,
        translate("sync.progress.fetchingTischOffensives"),
      );
    };

    const fetchDeliveryOptions = () => {
      this.props.deliveryOptions.fetch(fetchTischOffensives, this.onError);
      this.setCurrentSyncData(
        55,
        translate("sync.progress.fetchingDeliveryOptions"),
      );
    };

    const fetchCollInfo = () => {
      this.props.collInfo.fetch(fetchDeliveryOptions, this.onError);
      this.setCurrentSyncData(50, translate("sync.progress.fetchingCollInfo"));
    };

    const fetchProductsPrices = () => {
      this.props.productsPrices.fetch(fetchCollInfo, this.onError);
      this.setCurrentSyncData(45, translate("sync.progress.fetchingPrices"));
    };

    const fetchCustomerAddresses = () => {
      this.props.customerAddresses.fetch(fetchProductsPrices);
      this.setCurrentSyncData(
        40,
        translate("sync.progress.fetchingCustomerAddresses"),
      );
    };

    const fetchCustomerPriceGroups = () => {
      this.props.customerPriceGroups.fetch(fetchCustomerAddresses);
      this.setCurrentSyncData(
        30,
        translate("sync.progress.fetchingCustomersPriceGroups"),
      );
    };
    const fetchProductDeliveryPeriods = () => {
      this.props.productDeliveryPeriods.fetch(fetchCustomerPriceGroups);
      this.setCurrentSyncData(
        20,
        translate("sync.progress.fetchingProductDeliveryPeriods"),
      );
    };
    const fetchDeliveryPeriods = () => {
      this.props.deliveryPeriods.fetch(fetchProductDeliveryPeriods);
      this.setCurrentSyncData(
        10,
        translate("sync.progress.fetchingDeliveryPeriods"),
      );
    };
    const fetchingSeasons = () => {
      this.props.seasons.fetch(fetchDeliveryPeriods);
      this.setCurrentSyncData(5, translate("sync.progress.fetchingSeasons"));
    };

    fetchingSeasons();
  };

  setLowaCustomer = (c) => {
    console.log("setting lowa customer to", c);
    this.setState({ lowaCustomer: c });
    localStorage.setItem("lowaCustomer", JSON.stringify(c));
    console.log(this.state);
  };

  resyncData = () => {
    const { session } = this.state;
    this.downloadData(session);
  };

  createRecord = async (object, data, onsuccess, onerror) => {
    const { session } = this.state;
    await create(session, object, data).then(onsuccess).catch(onerror);
  };

  updateRecord = async (object, data, onsuccess, onerror) => {
    const { session } = this.state;
    await update(session, object, data).then(onsuccess).catch(onerror);
  };

  query = async (query) => {
    try {
      const { session } = this.state;

      const connection = getSalesforceConnectionByAccessToken(session);
      const data = await connection.query(query);
      console.log(data);
      return data.records;
    } catch (error) {
      console.log(error);
    }
  };

  createOrderForCustomer = ({
    user,
    customer,
    season,
    onCreate = () => {},
  }) => {
    if (!customer) return;

    const order = {
      id: generateOrderId(user.AgentNo__c),
      customerId: customer.id,
      season,
    };

    this.props.orders.createOrder(order);

    this.props.currentOrder.setCurrentOrder(order.id);
    onCreate(order.id);
  };

  logout = () => {
    this.setState({ session: false });
    this.setCurrentSyncData();
    this.props.toast.setToast({
      title: translate("sync.syncAuthError.title"),
      text: translate("sync.syncAuthError.description"),
    });
  };

  changePriceType = (priceType) => {
    if (priceType !== priceTypes.unit && priceType !== priceTypes.mrsp) return;
    this.setState({
      priceType,
    });
    localStorage.setItem("priceType", priceType);
  };

  enablePreloadingImages = (value) => {
    this.setState({ preloadingImagesEnable: value });
  };

  handleUpdateCacheForPrice = ({
    priceType,
    number,
    ean,
    priceGroup,
    season,
    value,
  }) => {
    const priceHasNoValue = !value || !value.value;
    if (priceHasNoValue) return;
    this.setState((state) => {
      const cacheForPrice = {
        ...state.cacheForPrice,
        [createCacheForPrice({
          season,
          number,
          ean,
          priceGroup,
          priceType,
        })]: value,
      };
      localStorage.setItem("cacheForPrice", JSON.stringify(cacheForPrice));
      return { cacheForPrice };
    });
  };

  render() {
    const marketingMode = false;
    const lowaMode =
      this.state.session &&
      this.state.session.user &&
      this.state.session.user.countryCode === "NB";

    return (
      <AppContext.Provider
        value={{
          actions: {
            addMultipleOrder: this.props.orders.addMultipleOrder,
            login: this.login,
            createOrder: this.props.orders.createOrder,
            createOrderForCustomer: this.createOrderForCustomer,
            updateOrder: this.props.orders.updateOrder,
            onAddOrUpdateOrderProduct:
              this.props.orders.onAddOrUpdateOrderProduct,
            closeOrder: this.props.orders.closeOrder,
            closeOrderButFailedToSync:
              this.props.orders.closeOrderButFailedToSync,
            removeProductFromOrder: this.props.orders.removeProductFromOrder,
            redownloadData: this.redownloadData,
            setToast: this.props.toast.setToast,
            logout: this.logout,
            changePriceType: this.changePriceType,
            setCurrentOrder: this.props.currentOrder.setCurrentOrder,
            removeOrder: this.props.orders.remove,
            resync: this.resyncData,
            query: this.query,
            updateRecord: this.updateRecord,
            createRecord: this.createRecord,
            enablePreloadingImages: this.enablePreloadingImages,
            onAddMultipleProductsToOrder:
              this.props.orders.onAddMultipleProductsToOrder,
            updateCacheForPrice: this.handleUpdateCacheForPrice,
            setLoadingImages: this.setLoadingImages,
            setLowaCustomer: this.setLowaCustomer,
          },
          state: {
            startupLoading: this.state.startupLoading,
            currentOrderId: this.props.currentOrder.value,
            toast: this.props.toast.value,
            priceType: this.state.priceType,
            sync: this.state.sync,
            preloadingImagesEnable: this.state.preloadingImagesEnable,
            cacheForPrice: this.state.cacheForPrice,
            online: this.state.online,
            marketing: marketingMode,
            lowa: lowaMode,
            lowaCustomer: this.state.lowaCustomer,
          },
          entities: {
            session: this.state.session,
            orders: this.props.orders.value,
            customers: this.props.customers.value,
            products: this.props.products.value,
            users: this.props.users.value,
            productDescriptions: this.props.productDescriptions.value,
            productsPrices: this.props.productsPrices.value,
            customerPriceGroups: this.props.customerPriceGroups.value,
            productDeliveryPeriods: this.props.productDeliveryPeriods.value,
            deliveryPeriods: this.props.deliveryPeriods.value,
            customerAddresses: this.props.customerAddresses.value,
            tischOffensives: this.props.tischOffensives.value,
            collInfo: this.props.collInfo.value,
            seasons: this.props.seasons.value,
            deliveryOptions: this.props.deliveryOptions.value,
            availableClusters: this.props.products.index.availableClusters,
          },
        }}
      >
        {this.props.children}
      </AppContext.Provider>
    );
  }
}

const hoc = compose(
  withTranslation(),
  withToast,
  withProductsPrices,
  withCustomers,
  withUsers,
  withProducts,
  withProductsDescriptions,
  withCurrentOrder,
  withOrders,
  withCustomersPriceGroup,
  withProductDeliveryPeriods,
  withDeliveryPeriods,
  withCustomerAddresses,
  withTischOffensives,
  withCollInfo,
  withDeliveryOptions,
  withSeasons,
);

export default hoc(AppProvider);
